import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Card,
  Chip,
  Grid,
  LinearProgress,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  CircularProgress,
  alpha,
  useTheme
} from '@mui/material';
import {
  Doughnut,
  Chart as MixedChart
} from 'react-chartjs-2';
import {
  ArcElement,
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip
} from 'chart.js';

import BoltIcon from '@mui/icons-material/Bolt';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import ExploreIcon from '@mui/icons-material/Explore';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import WaterDropIcon from '@mui/icons-material/WaterDrop';

import { APIService } from 'src/api/api';
import { getStoredUserTimezone, nowInTimezone } from 'src/utils/timezoneUtils';
import AnalyticsStatCard from 'src/components/dashboard/AnalyticsStatCard';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  LineController,
  BarController
);

const INVALID_METRIC_VALUE = -10000;
const FUEL_DIVISOR = 10;

const ANALYTICS_COLORS = {
  success: '#10B981',
  primary: '#3B82F6',
  warning: '#F59E0B',
  accent: '#8B5CF6',
  danger: '#EF4444',
  muted: '#94A3B8',
  text: '#102036'
};

const getPanelCardSx = (theme) => ({
  p: 2.5,
  borderRadius: 3,
  height: '100%',
  border: '1px solid',
  borderColor: alpha(theme.palette.divider, 0.08),
  boxShadow: '0 4px 24px 0 rgba(0,0,0,0.06)',
  backgroundColor: theme.palette.background.paper
});

const toMetricNumber = (value, options = {}) => {
  const {
    allowNegative = false,
    divisor = 1
  } = options;
  const parsed = Number(value);

  if (!Number.isFinite(parsed) || parsed === INVALID_METRIC_VALUE) {
    return null;
  }

  if (!allowNegative && parsed < 0) {
    return null;
  }

  return parsed / divisor;
};

const clampPercentage = (value) => {
  const parsed = Number(value);

  if (!Number.isFinite(parsed)) {
    return 0;
  }

  return Math.max(0, Math.min(100, parsed));
};

const getShare = (part, total) => {
  if (!Number.isFinite(part) || !Number.isFinite(total) || total <= 0) {
    return null;
  }

  return (part / total) * 100;
};

const formatNumber = (value, options = {}) => {
  if (!Number.isFinite(value)) {
    return '--';
  }

  return value.toLocaleString(undefined, options);
};

const formatMetric = (value, unit, options = {}) => {
  if (!Number.isFinite(value)) {
    return '--';
  }

  const formattedValue = formatNumber(value, options);
  return unit ? `${formattedValue} ${unit}` : formattedValue;
};

const formatDuration = (seconds) => formatMetric(
  Number.isFinite(seconds) ? seconds / 3600 : null,
  'h',
  { maximumFractionDigits: 1 }
);

const summarizeVehicleNames = (vehicles, limit = 2) => {
  if (!vehicles.length) {
    return 'No vehicles';
  }

  const names = vehicles.slice(0, limit).map((vehicle) => vehicle.name);

  if (vehicles.length <= limit) {
    return names.join(', ');
  }

  return `${names.join(', ')} +${vehicles.length - limit} more`;
};

const InsightMetricRow = ({
  label,
  value,
  caption,
  color,
  progress
}) => (
  <Box sx={{ py: 1 }}>
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: color
          }}
        />
        <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
          {label}
        </Typography>
      </Stack>

      <Box sx={{ textAlign: 'right' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary' }}>
          {value}
        </Typography>
        {caption ? (
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
            {caption}
          </Typography>
        ) : null}
      </Box>
    </Stack>

    {Number.isFinite(progress) ? (
      <LinearProgress
        variant="determinate"
        value={clampPercentage(progress)}
        sx={{
          mt: 1,
          height: 7,
          borderRadius: 999,
          bgcolor: alpha(color, 0.12),
          '& .MuiLinearProgress-bar': {
            borderRadius: 999,
            bgcolor: color
          }
        }}
      />
    ) : null}
  </Box>
);

const VehicleInsightRow = ({
  name,
  primaryValue,
  secondaryValue,
  color,
  progress
}) => (
  <Box
    sx={{
      py: 1.2,
      '&:not(:last-of-type)': {
        borderBottom: '1px solid',
        borderColor: 'divider'
      }
    }}
  >
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 800,
            color: 'text.primary',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {name}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            display: 'block',
            mt: 0.35
          }}
        >
          {secondaryValue}
        </Typography>
      </Box>

      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 800,
          color,
          flexShrink: 0
        }}
      >
        {primaryValue}
      </Typography>
    </Stack>

    {Number.isFinite(progress) ? (
      <LinearProgress
        variant="determinate"
        value={clampPercentage(progress)}
        sx={{
          mt: 1,
          height: 6,
          borderRadius: 999,
          bgcolor: alpha(color, 0.12),
          '& .MuiLinearProgress-bar': {
            borderRadius: 999,
            bgcolor: color
          }
        }}
      />
    ) : null}
  </Box>
);

InsightMetricRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  caption: PropTypes.string,
  color: PropTypes.string.isRequired,
  progress: PropTypes.number
};

InsightMetricRow.defaultProps = {
  caption: undefined,
  progress: undefined
};

VehicleInsightRow.propTypes = {
  name: PropTypes.string.isRequired,
  primaryValue: PropTypes.string.isRequired,
  secondaryValue: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  progress: PropTypes.number
};

VehicleInsightRow.defaultProps = {
  progress: undefined
};

const normalizeVehicleAnalytics = (vehicle) => {
  const mileage = toMetricNumber(vehicle.mw?.mileage);
  const totalFuel = toMetricNumber(vehicle.fuel?.fuelConsumption, { divisor: FUEL_DIVISOR });
  const idleFuel = toMetricNumber(vehicle.fuel?.fuelConsumptionWOMovement, { divisor: FUEL_DIVISOR });
  const fuelPer100 = toMetricNumber(vehicle.fuel?.fuelCons100, { divisor: FUEL_DIVISOR });
  const layupPercent = clampPercentage(toMetricNumber(vehicle.mw?.layUpPercent) ?? 0);
  const draining = toMetricNumber(vehicle.fuel?.draining, { divisor: FUEL_DIVISOR }) ?? 0;
  const kmPerLitre = mileage > 0 && totalFuel > 0 ? mileage / totalFuel : null;
  const idleFuelShare = getShare(idleFuel, totalFuel);
  const hasMissingFuelData = mileage > 0 && totalFuel === null;
  const noMovementFuelBurn = mileage === 0 && totalFuel > 0;
  const highIdleFuelShare = (idleFuelShare ?? 0) >= 40;
  const highLayup = layupPercent >= 70;
  const highConsumption = (fuelPer100 ?? 0) >= 45;

  let attentionLabel = null;
  let attentionScore = 0;
  let attentionProgress = null;
  let attentionType = null;

  if (draining > 0) {
    attentionType = 'draining';
    attentionLabel = `${formatMetric(draining, 'L', { maximumFractionDigits: 1 })} drained`;
    attentionScore = 100 + draining;
  } else if (hasMissingFuelData) {
    attentionType = 'missingFuel';
    attentionLabel = 'Fuel telemetry missing';
    attentionScore = 90;
  } else if (noMovementFuelBurn) {
    attentionType = 'noMovementFuel';
    attentionLabel = `${formatMetric(totalFuel, 'L', { maximumFractionDigits: 1 })} burned with no movement`;
    attentionScore = 80 + totalFuel;
  } else if (highIdleFuelShare) {
    attentionType = 'idleFuelShare';
    attentionLabel = `${formatMetric(idleFuelShare, '%', { maximumFractionDigits: 1 })} idle fuel share`;
    attentionScore = 70 + idleFuelShare;
    attentionProgress = idleFuelShare;
  } else if (highLayup) {
    attentionType = 'layup';
    attentionLabel = `${formatMetric(layupPercent, '%', { maximumFractionDigits: 1 })} layup time`;
    attentionScore = 60 + layupPercent;
    attentionProgress = layupPercent;
  } else if (highConsumption) {
    attentionType = 'consumption';
    attentionLabel = `${formatMetric(fuelPer100, 'L/100 km', { maximumFractionDigits: 1 })} consumption`;
    attentionScore = 50 + fuelPer100;
  }

  return {
    name: vehicle.name || `Vehicle ${vehicle.vehicleID}`,
    mileage,
    totalFuel,
    fuelPer100,
    kmPerLitre,
    draining,
    attentionType,
    attentionLabel,
    attentionScore,
    attentionProgress
  };
};

const FleetAnalytics = () => {
  const theme = useTheme();
  const history = useHistory();

  const [days, setDays] = useState('today');
  const [historicalLoading, setHistoricalLoading] = useState(true);
  const [historicalData, setHistoricalData] = useState(null);
  const [historicalError, setHistoricalError] = useState(false);
  const [liveLoading, setLiveLoading] = useState(true);
  const [liveError, setLiveError] = useState(false);
  const [liveData, setLiveData] = useState([]);

  const fetchStatistics = useCallback(async () => {
    setHistoricalLoading(true);
    setHistoricalError(false);

    try {
      const timezone = getStoredUserTimezone();
      const currentDay = nowInTimezone(timezone);
      let rangeStart = currentDay.clone().startOf('day');
      let rangeEnd = currentDay.clone().endOf('day');
      if (days === 'yesterday') {
        rangeStart = currentDay.clone().subtract(1, 'day').startOf('day');
        rangeEnd = currentDay.clone().subtract(1, 'day').endOf('day');
      } else if (days === 'last3') {
        rangeStart = currentDay.clone().subtract(2, 'days').startOf('day');
      }
      const payload = {
        timeBegin: rangeStart.unix(),
        timeEnd: rangeEnd.unix(),
        timezone,
        tz: timezone
      };
      const res = await APIService.getFleetStatisticsDashboard(payload);

      if (res?.success) {
        setHistoricalData(res.result?.data || null);
      } else {
        setHistoricalData(null);
        setHistoricalError(true);
      }
    } catch (err) {
      setHistoricalData(null);
      setHistoricalError(true);
    } finally {
      setHistoricalLoading(false);
    }
  }, [days]);

  const fetchLiveState = useCallback(async () => {
    try {
      const res = await APIService.getFleetCurrentState();
      if (res?.success && res.result?.data) {
        setLiveData(res.result.data);
        setLiveError(false);
      } else {
        setLiveData([]);
        setLiveError(true);
      }
    } catch (err) {
      setLiveData([]);
      setLiveError(true);
      console.error('Failed to fetch live fleet state', err);
    } finally {
      setLiveLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatistics();
  }, [fetchStatistics]);

  useEffect(() => {
    fetchLiveState();
  }, [fetchLiveState]);

  const fleetSummary = useMemo(() => {
    if (!historicalData) {
      return null;
    }

    const totalMileage = toMetricNumber(historicalData.totalMw?.totalMileage) ?? 0;
    const totalFuel = toMetricNumber(historicalData.totalFuel?.totalConsumption, { divisor: FUEL_DIVISOR }) ?? 0;
    const idleFuel = toMetricNumber(historicalData.totalFuel?.totalConsumptionWOMovement, { divisor: FUEL_DIVISOR }) ?? 0;
    const movingFuel = toMetricNumber(historicalData.totalFuel?.totalConsumptionOnMove, { divisor: FUEL_DIVISOR })
      ?? Math.max(totalFuel - idleFuel, 0);
    const refuelled = toMetricNumber(historicalData.totalFuel?.totalRefuelling, { divisor: FUEL_DIVISOR }) ?? 0;
    const drained = toMetricNumber(historicalData.totalFuel?.totalDraining, { divisor: FUEL_DIVISOR }) ?? 0;
    const kmPerLitre = toMetricNumber(historicalData.totalMw?.totalMileagePerLitre)
      ?? (totalFuel > 0 ? totalMileage / totalFuel : null);
    const moveOnlyKmPerLitre = toMetricNumber(historicalData.totalMw?.totalMovementMileagePerLitre);
    const avgConsumption100 = toMetricNumber(historicalData.totalFuel?.averageConsumption100, { divisor: FUEL_DIVISOR });
    const movePercent = clampPercentage(toMetricNumber(historicalData.totalMw?.totalMoveTimePercent) ?? 0);
    const idlePercent = clampPercentage(toMetricNumber(historicalData.totalMw?.totalWorkedNoMovementPercent) ?? 0);
    const layupPercent = clampPercentage(toMetricNumber(historicalData.totalMw?.totalLayUpTimePercent) ?? 0);
    const speedingMileage = toMetricNumber(historicalData.totalMw?.totalMileageSpeeding) ?? 0;
    const totalMoveTime = toMetricNumber(historicalData.totalMw?.totalMoveTime);
    const totalWorkedNoMovementTime = toMetricNumber(historicalData.totalMw?.totalWorkedNoMovementTime);
    const totalLayUpTime = toMetricNumber(historicalData.totalMw?.totalLayUpTime);
    const totalWorkedTime = toMetricNumber(historicalData.totalMw?.totalWorked);
    const secondaryEngineOnTime = toMetricNumber(historicalData.totalAe?.totalUnivInputOnTime);
    const productiveFuelShare = getShare(movingFuel, totalFuel);
    const idleFuelShare = getShare(idleFuel, totalFuel);

    return {
      totalMileage,
      totalFuel,
      idleFuel,
      movingFuel,
      refuelled,
      drained,
      kmPerLitre,
      moveOnlyKmPerLitre,
      avgConsumption100,
      movePercent,
      idlePercent,
      layupPercent,
      speedingMileage,
      totalMoveTime,
      totalWorkedNoMovementTime,
      totalLayUpTime,
      totalWorkedTime,
      secondaryEngineOnTime,
      productiveFuelShare,
      idleFuelShare
    };
  }, [historicalData]);

  const normalizedVehicles = useMemo(
    () => (historicalData?.vehicleDataList || []).map(normalizeVehicleAnalytics),
    [historicalData]
  );

  const liveStats = useMemo(() => {
    let moving = 0;
    let idling = 0;
    let parked = 0;

    liveData.forEach((vehicle) => {
      if (!vehicle.currentIgn) {
        parked += 1;
      } else if (vehicle.speed === 0) {
        idling += 1;
      } else {
        moving += 1;
      }
    });

    return {
      moving,
      idling,
      parked,
      total: liveData.length
    };
  }, [liveData]);

  const historicalDoughnutData = useMemo(() => {
    if (!fleetSummary) {
      return null;
    }

    return {
      labels: ['Moving', 'Idling', 'Offline'],
      datasets: [
        {
          data: [
            fleetSummary.movePercent,
            fleetSummary.idlePercent,
            fleetSummary.layupPercent
          ],
          backgroundColor: [
            ANALYTICS_COLORS.success,
            ANALYTICS_COLORS.warning,
            theme.palette.grey[400]
          ],
          borderWidth: 0,
          hoverOffset: 4
        }
      ]
    };
  }, [fleetSummary, theme.palette.grey]);

  const fuelProductivityData = useMemo(() => {
    if (!fleetSummary || fleetSummary.totalFuel <= 0) {
      return null;
    }

    return {
      labels: ['Productive fuel', 'Idle / no movement'],
      datasets: [
        {
          data: [fleetSummary.movingFuel, fleetSummary.idleFuel],
          backgroundColor: [
            ANALYTICS_COLORS.primary,
            ANALYTICS_COLORS.warning
          ],
          borderWidth: 0,
          hoverOffset: 4
        }
      ]
    };
  }, [fleetSummary]);

  const mixedChartData = useMemo(() => {
    const topVehicles = [...normalizedVehicles]
      .filter((vehicle) => (vehicle.mileage || 0) > 0)
      .sort((first, second) => (second.mileage || 0) - (first.mileage || 0))
      .slice(0, 10);

    if (!topVehicles.length) {
      return null;
    }

    return {
      labels: topVehicles.map((vehicle) => vehicle.name),
      datasets: [
        {
          type: 'bar',
          label: 'Distance Covered (km)',
          backgroundColor: alpha(ANALYTICS_COLORS.primary, 0.8),
          yAxisID: 'y',
          data: topVehicles.map((vehicle) => vehicle.mileage || 0),
          borderRadius: 6,
          barPercentage: 0.62
        },
        {
          type: 'line',
          label: 'Fuel Consumed (L)',
          borderColor: ANALYTICS_COLORS.accent,
          backgroundColor: ANALYTICS_COLORS.accent,
          borderWidth: 3,
          fill: false,
          yAxisID: 'y1',
          data: topVehicles.map((vehicle) => vehicle.totalFuel || 0),
          tension: 0.32,
          pointRadius: 4,
          pointHoverRadius: 6
        }
      ]
    };
  }, [normalizedVehicles]);

  const efficiencyLeaders = useMemo(() => {
    const leaders = [...normalizedVehicles]
      .filter((vehicle) => vehicle.kmPerLitre > 0 && vehicle.mileage > 0)
      .sort((first, second) => second.kmPerLitre - first.kmPerLitre)
      .slice(0, 5);

    const bestKmPerLitre = leaders[0]?.kmPerLitre || 0;

    return leaders.map((vehicle) => ({
      ...vehicle,
      progress: bestKmPerLitre > 0 ? (vehicle.kmPerLitre / bestKmPerLitre) * 100 : null
    }));
  }, [normalizedVehicles]);

  const efficiencyLeaderChartData = useMemo(() => {
    if (!efficiencyLeaders.length) {
      return null;
    }

    return {
      labels: efficiencyLeaders.map((vehicle) => vehicle.name),
      datasets: [
        {
          label: 'km/L',
          data: efficiencyLeaders.map((vehicle) => vehicle.kmPerLitre),
          backgroundColor: [
            alpha(ANALYTICS_COLORS.success, 0.92),
            alpha(ANALYTICS_COLORS.success, 0.82),
            alpha(ANALYTICS_COLORS.success, 0.72),
            alpha(ANALYTICS_COLORS.success, 0.62),
            alpha(ANALYTICS_COLORS.success, 0.52)
          ],
          borderRadius: 10,
          borderSkipped: false,
          barThickness: 18
        }
      ]
    };
  }, [efficiencyLeaders]);

  const efficiencyLeaderSummary = useMemo(() => {
    if (!efficiencyLeaders.length) {
      return null;
    }

    const bestVehicle = efficiencyLeaders[0];
    const averageKmPerLitre = efficiencyLeaders.reduce((total, vehicle) => total + vehicle.kmPerLitre, 0) / efficiencyLeaders.length;

    return {
      bestVehicle,
      averageKmPerLitre
    };
  }, [efficiencyLeaders]);

  const drainVehicles = useMemo(
    () => [...normalizedVehicles]
      .filter((vehicle) => vehicle.draining > 0)
      .sort((first, second) => second.draining - first.draining),
    [normalizedVehicles]
  );

  const drainAlertChartData = useMemo(() => {
    const topDrainVehicles = drainVehicles.slice(0, 5);

    if (!topDrainVehicles.length) {
      return null;
    }

    return {
      labels: topDrainVehicles.map((vehicle) => vehicle.name),
      datasets: [
        {
          label: 'Drained (L)',
          data: topDrainVehicles.map((vehicle) => vehicle.draining),
          backgroundColor: [
            alpha(ANALYTICS_COLORS.danger, 0.95),
            alpha(ANALYTICS_COLORS.danger, 0.85),
            alpha(ANALYTICS_COLORS.danger, 0.75),
            alpha(ANALYTICS_COLORS.danger, 0.65),
            alpha(ANALYTICS_COLORS.danger, 0.55)
          ],
          borderRadius: 10,
          borderSkipped: false,
          barThickness: 18
        }
      ]
    };
  }, [drainVehicles]);

  const drainSummary = useMemo(() => {
    if (!drainVehicles.length) {
      return null;
    }

    const totalDrained = drainVehicles.reduce((total, vehicle) => total + vehicle.draining, 0);
    const topVehicle = drainVehicles[0];

    return {
      totalDrained,
      vehicleCount: drainVehicles.length,
      topVehicle,
      topVehicleNames: summarizeVehicleNames(drainVehicles, 2)
    };
  }, [drainVehicles]);

  const operationalRows = useMemo(() => {
    if (!fleetSummary) {
      return [];
    }

    return [
      {
        label: 'Moving',
        value: formatMetric(fleetSummary.movePercent, '%', { maximumFractionDigits: 1 }),
        caption: formatDuration(fleetSummary.totalMoveTime),
        color: ANALYTICS_COLORS.success,
        progress: fleetSummary.movePercent
      },
      {
        label: 'Idling',
        value: formatMetric(fleetSummary.idlePercent, '%', { maximumFractionDigits: 1 }),
        caption: formatDuration(fleetSummary.totalWorkedNoMovementTime),
        color: ANALYTICS_COLORS.warning,
        progress: fleetSummary.idlePercent
      },
      {
        label: 'Offline',
        value: formatMetric(fleetSummary.layupPercent, '%', { maximumFractionDigits: 1 }),
        caption: formatDuration(fleetSummary.totalLayUpTime),
        color: theme.palette.grey[500],
        progress: fleetSummary.layupPercent
      }
    ];
  }, [fleetSummary, theme.palette.grey]);

  const fuelBreakdownRows = useMemo(() => {
    if (!fleetSummary) {
      return [];
    }

    return [
      {
        label: 'Productive fuel',
        value: formatMetric(fleetSummary.movingFuel, 'L', { maximumFractionDigits: 1 }),
        caption: fleetSummary.productiveFuelShare !== null
          ? `${formatMetric(fleetSummary.productiveFuelShare, '%', { maximumFractionDigits: 1 })} of total`
          : 'No share available',
        color: ANALYTICS_COLORS.primary,
        progress: fleetSummary.productiveFuelShare
      },
      {
        label: 'Idle fuel',
        value: formatMetric(fleetSummary.idleFuel, 'L', { maximumFractionDigits: 1 }),
        caption: fleetSummary.idleFuelShare !== null
          ? `${formatMetric(fleetSummary.idleFuelShare, '%', { maximumFractionDigits: 1 })} of total`
          : 'No share available',
        color: ANALYTICS_COLORS.warning,
        progress: fleetSummary.idleFuelShare
      },
      {
        label: 'Refuelled',
        value: formatMetric(fleetSummary.refuelled, 'L', { maximumFractionDigits: 1 }),
        caption: 'In the selected window',
        color: ANALYTICS_COLORS.success
      },
      {
        label: 'Drained',
        value: formatMetric(fleetSummary.drained, 'L', { maximumFractionDigits: 1 }),
        caption: 'Needs follow-up when above zero',
        color: fleetSummary.drained > 0 ? ANALYTICS_COLORS.danger : ANALYTICS_COLORS.muted
      }
    ];
  }, [fleetSummary]);

  const renderPanelState = (message, minHeight = 240) => (
    <Box
      sx={{
        minHeight,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 2,
        textAlign: 'center'
      }}
    >
      {message === 'Failed to load' ? (
        <WarningAmberIcon sx={{ fontSize: 48, color: 'warning.main' }} />
      ) : null}
      {message === 'Loading...' ? <CircularProgress size={40} /> : null}
      <Typography variant="subtitle1" sx={{ color: 'text.secondary', fontWeight: 700 }}>
        {message}
      </Typography>
    </Box>
  );

  const getApiValue = (loadingState, errorState, successValue) => {
    if (loadingState) {
      return 'Loading...';
    }

    if (errorState) {
      return 'Failed to load';
    }

    return successValue;
  };

  const drainVehicleCount = drainSummary?.vehicleCount ?? 0;
  let drainHelperText;

  if (!historicalLoading && !historicalError) {
    drainHelperText = drainVehicleCount > 0
      ? `Alert ${drainSummary?.topVehicleNames}`
      : 'No drain alerts in this window';
  }

  const statCards = useMemo(() => ([
    {
      key: 'distance',
      title: 'Total Distance',
      value: getApiValue(
        historicalLoading,
        historicalError,
        formatMetric(fleetSummary?.totalMileage, 'km', { maximumFractionDigits: 1 })
      ),
      helperText: !historicalLoading && !historicalError
        ? `Speeding ${formatMetric(fleetSummary?.speedingMileage, 'km', { maximumFractionDigits: 1 })}`
        : undefined,
      icon: (<DirectionsCarIcon />),
      color: ANALYTICS_COLORS.success
    },
    {
      key: 'fuel',
      title: 'Fuel Consumed',
      value: getApiValue(
        historicalLoading,
        historicalError,
        formatMetric(fleetSummary?.totalFuel, 'L', { maximumFractionDigits: 1 })
      ),
      helperText: !historicalLoading && !historicalError
        ? `Refuelled ${formatMetric(fleetSummary?.refuelled, 'L', { maximumFractionDigits: 1 })}`
        : undefined,
      icon: (<LocalGasStationIcon />),
      color: ANALYTICS_COLORS.primary
    },
    {
      key: 'efficiency',
      title: 'Fleet Efficiency',
      value: getApiValue(
        historicalLoading,
        historicalError,
        formatMetric(fleetSummary?.kmPerLitre, 'km/L', { maximumFractionDigits: 2 })
      ),
      metaItems: !historicalLoading && !historicalError && Number.isFinite(fleetSummary?.moveOnlyKmPerLitre) ? [
        {
          value: formatNumber(fleetSummary.moveOnlyKmPerLitre, { maximumFractionDigits: 2 }),
          label: 'move-only km/L',
          color: ANALYTICS_COLORS.success
        }
      ] : [],
      helperText: !historicalLoading && !historicalError
        ? `Avg ${formatMetric(fleetSummary?.avgConsumption100, 'L/100 km', { maximumFractionDigits: 1 })}`
        : undefined,
      icon: (<BoltIcon />),
      color: ANALYTICS_COLORS.accent
    },
    {
      key: 'idle-fuel',
      title: 'Idle Fuel',
      value: getApiValue(
        historicalLoading,
        historicalError,
        formatMetric(fleetSummary?.idleFuel, 'L', { maximumFractionDigits: 1 })
      ),
      metaItems: !historicalLoading && !historicalError && Number.isFinite(fleetSummary?.idleFuelShare) ? [
        {
          value: formatMetric(fleetSummary.idleFuelShare, '%', { maximumFractionDigits: 1 }),
          label: 'of fuel',
          color: ANALYTICS_COLORS.warning
        }
      ] : [],
      helperText: !historicalLoading && !historicalError
        ? `Productive ${formatMetric(fleetSummary?.movingFuel, 'L', { maximumFractionDigits: 1 })}`
        : undefined,
      icon: (<WaterDropIcon />),
      color: ANALYTICS_COLORS.warning
    },
    {
      key: 'draining',
      title: 'Fuel Draining',
      value: getApiValue(
        historicalLoading,
        historicalError,
        formatMetric(drainSummary?.totalDrained ?? fleetSummary?.drained, 'L', { maximumFractionDigits: 1 })
      ),
      metaItems: !historicalLoading && !historicalError ? [
        {
          value: drainVehicleCount,
          label: 'vehicles',
          color: drainVehicleCount > 0 ? ANALYTICS_COLORS.danger : ANALYTICS_COLORS.muted
        }
      ] : [],
      helperText: drainHelperText,
      icon: (<WarningAmberIcon />),
      color: drainVehicleCount > 0 ? ANALYTICS_COLORS.danger : ANALYTICS_COLORS.muted
    },
    {
      key: 'live',
      title: 'Live Fleet Status',
      value: getApiValue(liveLoading, liveError, liveStats.total),
      metaItems: !liveLoading && !liveError ? [
        {
          value: liveStats.moving,
          label: 'moving',
          color: ANALYTICS_COLORS.success
        },
        {
          value: liveStats.idling,
          label: 'idling',
          color: ANALYTICS_COLORS.warning
        },
        {
          value: liveStats.parked,
          label: 'offline',
          color: theme.palette.text.secondary
        }
      ] : [],
      icon: (<ExploreIcon />),
      color: ANALYTICS_COLORS.accent,
      onClick: !liveLoading && !liveError ? () => history.push('/app/fleet-management') : undefined
    }
  ]), [drainHelperText, drainVehicleCount, drainSummary, fleetSummary, historicalError, historicalLoading, history, liveError, liveLoading, liveStats, theme.palette.text.secondary]);

  return (
    <Box sx={{ pt: 1, pb: 6 }}>
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 800,
              color: ANALYTICS_COLORS.text,
              letterSpacing: -0.4
            }}
          >
            Fleet Analytics
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Unified fleet KPIs, utilization, fuel productivity, and attention queues
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'inline-flex',
            bgcolor: 'background.paper',
            p: 0.5,
            borderRadius: 3,
            boxShadow: '0 2px 10px 0 rgba(0,0,0,0.04)',
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <ToggleButtonGroup
            value={days}
            exclusive
            onChange={(event, newValue) => {
              if (newValue !== null) {
                setDays(newValue);
              }
            }}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                minHeight: 40,
                px: 2.5,
                border: 0,
                textTransform: 'none',
                fontWeight: 600,
                color: 'text.secondary',
                borderRadius: 2,
                '&:hover': {
                  bgcolor: alpha(theme.palette.action.hover, 0.5)
                },
                '&.Mui-selected': {
                  color: 'primary.main',
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  boxShadow: '0 2px 8px 0 rgba(0,0,0,0.04)'
                }
              }
            }}
          >
            <ToggleButton value="today">Today</ToggleButton>
            <ToggleButton value="yesterday">Yesterday</ToggleButton>
            <ToggleButton value="last3">Last 3 Days</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        {statCards.map((card) => (
          <Grid item xs={12} sm={6} md={4} lg={2} key={card.key}>
            <AnalyticsStatCard {...card} compact />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={4}>
          <Card sx={getPanelCardSx(theme)}>
            <Box sx={{ mb: 2.5 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  lineHeight: 1.2
                }}
              >
                Fleet Time Overview
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Historical operating split with percent and hours
              </Typography>
            </Box>

            <Box
              sx={{
                minHeight: 248,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {historicalLoading ? renderPanelState('Loading...', 248) : null}
              {!historicalLoading && historicalError ? renderPanelState('Failed to load', 248) : null}
              {!historicalLoading && !historicalError && !historicalDoughnutData ? renderPanelState('No data available', 248) : null}

              {!historicalLoading && !historicalError && historicalDoughnutData ? (
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  alignItems="center"
                  sx={{ width: '100%' }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      width: { xs: '100%', sm: '52%' },
                      minWidth: 0,
                      height: 230,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Box sx={{ width: '100%', height: 230 }}>
                      <Doughnut
                        data={historicalDoughnutData}
                        options={{
                          cutout: '76%',
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: {
                              display: false
                            }
                          }
                        }}
                      />
                    </Box>

                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none'
                      }}
                    >
                      <Typography variant="h3" sx={{ fontWeight: 900, color: ANALYTICS_COLORS.text }}>
                        {formatNumber(fleetSummary?.movePercent, { maximumFractionDigits: 1 })}
                        %
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                        Moving time
                      </Typography>
                    </Box>
                  </Box>

                  <Stack
                    spacing={1}
                    sx={{
                      width: { xs: '100%', sm: '48%' }
                    }}
                  >
                    {operationalRows.map((row) => (
                      <Box
                        key={row.label}
                        sx={{
                          p: 1.2,
                          borderRadius: 2.5,
                          border: '1px solid',
                          borderColor: alpha(row.color, 0.18),
                          bgcolor: alpha(row.color, 0.06)
                        }}
                      >
                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: row.color
                              }}
                            />
                            <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                              {row.label}
                            </Typography>
                          </Stack>

                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary' }}>
                              {row.value}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                              {row.caption}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                </Stack>
              ) : null}
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} lg={8}>
          <Card sx={getPanelCardSx(theme)}>
            <Box sx={{ mb: 2.5 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  lineHeight: 1.2
                }}
              >
                Top 10 Performers
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Distance covered versus fuel consumed
              </Typography>
            </Box>

            <Box sx={{ height: 380 }}>
              {historicalLoading ? renderPanelState('Loading...', 320) : null}
              {!historicalLoading && historicalError ? renderPanelState('Failed to load', 320) : null}
              {!historicalLoading && !historicalError && !mixedChartData ? renderPanelState('No ranked vehicles yet', 320) : null}

              {!historicalLoading && !historicalError && mixedChartData ? (
                <MixedChart
                  type="bar"
                  data={mixedChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: {
                      mode: 'index',
                      intersect: false
                    },
                    plugins: {
                      legend: {
                        position: 'top',
                        labels: {
                          usePointStyle: true,
                          boxWidth: 8,
                          font: { weight: 700 }
                        }
                      }
                    },
                    scales: {
                      y: {
                        type: 'linear',
                        display: true,
                        position: 'left',
                        title: {
                          display: true,
                          text: 'Distance Covered (km)',
                          color: theme.palette.text.secondary,
                          font: { weight: 700 }
                        },
                        grid: {
                          borderDash: [4, 4],
                          color: alpha(theme.palette.divider, 0.8)
                        }
                      },
                      y1: {
                        type: 'linear',
                        display: true,
                        position: 'right',
                        title: {
                          display: true,
                          text: 'Fuel Consumed (L)',
                          color: theme.palette.text.secondary,
                          font: { weight: 700 }
                        },
                        grid: {
                          drawOnChartArea: false
                        }
                      },
                      x: {
                        grid: {
                          display: false
                        },
                        ticks: {
                          color: theme.palette.text.secondary
                        }
                      }
                    }
                  }}
                />
              ) : null}
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={getPanelCardSx(theme)}>
            <Box sx={{ mb: 2.5 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  lineHeight: 1.2
                }}
              >
                Fuel Productivity
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Productive burn versus idle consumption
              </Typography>
            </Box>

            <Box
              sx={{
                position: 'relative',
                minHeight: 240,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {historicalLoading ? renderPanelState('Loading...', 240) : null}
              {!historicalLoading && historicalError ? renderPanelState('Failed to load', 240) : null}
              {!historicalLoading && !historicalError && !fuelProductivityData ? renderPanelState('No fuel data available', 240) : null}

              {!historicalLoading && !historicalError && fuelProductivityData ? (
                <Box sx={{ width: '100%', height: 240 }}>
                  <Doughnut
                    data={fuelProductivityData}
                    options={{
                      cutout: '76%',
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: 'bottom',
                          labels: {
                            usePointStyle: true,
                            boxWidth: 8,
                            font: { weight: 700 }
                          }
                        }
                      }
                    }}
                  />
                </Box>
              ) : null}

              {!historicalLoading && !historicalError && fuelProductivityData ? (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'none'
                  }}
                >
                  <Typography variant="h3" sx={{ fontWeight: 900, color: ANALYTICS_COLORS.text }}>
                    {formatNumber(fleetSummary?.productiveFuelShare, { maximumFractionDigits: 1 })}
                    %
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                    Productive fuel
                  </Typography>
                </Box>
              ) : null}
            </Box>

            {!historicalLoading && !historicalError && fuelBreakdownRows.length > 0 ? (
              <>
                <Box sx={{ mt: 1.5 }}>
                  {fuelBreakdownRows.map((row) => (
                    <InsightMetricRow
                      key={row.label}
                      label={row.label}
                      value={row.value}
                      caption={row.caption}
                      color={row.color}
                      progress={row.progress}
                    />
                  ))}
                </Box>

                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
                  <Chip
                    size="small"
                    label={`Aux on ${formatDuration(fleetSummary?.secondaryEngineOnTime)}`}
                    sx={{
                      bgcolor: alpha(ANALYTICS_COLORS.accent, 0.1),
                      color: ANALYTICS_COLORS.accent,
                      fontWeight: 700
                    }}
                  />
                  <Chip
                    size="small"
                    label={`Worked ${formatDuration(fleetSummary?.totalWorkedTime)}`}
                    sx={{
                      bgcolor: alpha(ANALYTICS_COLORS.success, 0.1),
                      color: ANALYTICS_COLORS.success,
                      fontWeight: 700
                    }}
                  />
                </Stack>
              </>
            ) : null}
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={getPanelCardSx(theme)}>
            <Box sx={{ mb: 2.5 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  lineHeight: 1.2
                }}
              >
                Efficiency Leaders
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Best mileage delivered per litre consumed
              </Typography>
            </Box>

            {historicalLoading ? renderPanelState('Loading...', 380) : null}
            {!historicalLoading && historicalError ? renderPanelState('Failed to load', 380) : null}
            {!historicalLoading && !historicalError && !efficiencyLeaderChartData ? renderPanelState('No efficiency leaders yet', 380) : null}

            {!historicalLoading && !historicalError && efficiencyLeaderChartData ? (
              <>
                <Box sx={{ height: 220 }}>
                  <MixedChart
                    type="bar"
                    data={efficiencyLeaderChartData}
                    options={{
                      indexAxis: 'y',
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          display: false
                        }
                      },
                      scales: {
                        x: {
                          grid: {
                            color: alpha(theme.palette.divider, 0.7)
                          },
                          ticks: {
                            color: theme.palette.text.secondary,
                            callback: (value) => `${value} km/L`
                          }
                        },
                        y: {
                          grid: {
                            display: false
                          },
                          ticks: {
                            color: theme.palette.text.secondary
                          }
                        }
                      }
                    }}
                  />
                </Box>

                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.5, mb: 1 }}>
                  <Chip
                    size="small"
                    label={`Best ${formatMetric(efficiencyLeaderSummary?.bestVehicle?.kmPerLitre, 'km/L', { maximumFractionDigits: 2 })}`}
                    sx={{
                      bgcolor: alpha(ANALYTICS_COLORS.success, 0.1),
                      color: ANALYTICS_COLORS.success,
                      fontWeight: 700
                    }}
                  />
                  <Chip
                    size="small"
                    label={`Avg ${formatMetric(efficiencyLeaderSummary?.averageKmPerLitre, 'km/L', { maximumFractionDigits: 2 })}`}
                    sx={{
                      bgcolor: alpha(ANALYTICS_COLORS.primary, 0.1),
                      color: ANALYTICS_COLORS.primary,
                      fontWeight: 700
                    }}
                  />
                </Stack>

                <Box>
                  {efficiencyLeaders.slice(0, 3).map((vehicle) => (
                    <VehicleInsightRow
                      key={vehicle.name}
                      name={vehicle.name}
                      primaryValue={formatMetric(vehicle.kmPerLitre, 'km/L', { maximumFractionDigits: 2 })}
                      secondaryValue={`${formatMetric(vehicle.mileage, 'km', { maximumFractionDigits: 1 })} | ${formatMetric(vehicle.fuelPer100, 'L/100 km', { maximumFractionDigits: 1 })}`}
                      color={ANALYTICS_COLORS.success}
                      progress={vehicle.progress}
                    />
                  ))}
                </Box>
              </>
            ) : null}
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={getPanelCardSx(theme)}>
            <Box sx={{ mb: 2.5 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  lineHeight: 1.2
                }}
              >
                Fuel Drain Alerts
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Immediate warning list for vehicles with drain events
              </Typography>
            </Box>

            {historicalLoading ? renderPanelState('Loading...', 380) : null}
            {!historicalLoading && historicalError ? renderPanelState('Failed to load', 380) : null}
            {!historicalLoading && !historicalError && !drainAlertChartData ? renderPanelState('No drain alerts in selected window', 380) : null}

            {!historicalLoading && !historicalError && drainAlertChartData ? (
              <>
                <Box
                  sx={{
                    p: 1.4,
                    borderRadius: 2.5,
                    border: '1px solid',
                    borderColor: alpha(ANALYTICS_COLORS.danger, 0.2),
                    bgcolor: alpha(ANALYTICS_COLORS.danger, 0.06),
                    mb: 2
                  }}
                >
                  <Stack spacing={1.1}>
                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5}>
                      <Box>
                        <Typography
                          variant="overline"
                          sx={{
                            color: ANALYTICS_COLORS.danger,
                            fontWeight: 800,
                            letterSpacing: 0.7
                          }}
                        >
                          Immediate Review
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 900, color: ANALYTICS_COLORS.text, lineHeight: 1.1 }}>
                          {formatMetric(drainSummary?.totalDrained, 'L', { maximumFractionDigits: 1 })}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                          {formatNumber(drainSummary?.vehicleCount, { maximumFractionDigits: 0 })}
                          {' '}
                          vehicles reporting drain
                        </Typography>
                      </Box>

                      <Chip
                        size="small"
                        label={drainSummary?.topVehicle ? `Highest ${drainSummary.topVehicle.name}` : 'Drain alert'}
                        sx={{
                          bgcolor: alpha(ANALYTICS_COLORS.danger, 0.12),
                          color: ANALYTICS_COLORS.danger,
                          fontWeight: 700
                        }}
                      />
                    </Stack>

                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                      Vehicles:
                      {' '}
                      {drainSummary?.topVehicleNames}
                    </Typography>
                  </Stack>
                </Box>

                <Box sx={{ height: 220, mb: 1.5 }}>
                  <MixedChart
                    type="bar"
                    data={drainAlertChartData}
                    options={{
                      indexAxis: 'y',
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          display: false
                        }
                      },
                      scales: {
                        x: {
                          grid: {
                            color: alpha(theme.palette.divider, 0.7)
                          },
                          ticks: {
                            color: theme.palette.text.secondary,
                            callback: (value) => `${value} L`
                          }
                        },
                        y: {
                          grid: {
                            display: false
                          },
                          ticks: {
                            color: theme.palette.text.secondary
                          }
                        }
                      }
                    }}
                  />
                </Box>

                <Box>
                  {drainVehicles.slice(0, 3).map((vehicle) => (
                    <VehicleInsightRow
                      key={vehicle.name}
                      name={vehicle.name}
                      primaryValue={formatMetric(vehicle.draining, 'L', { maximumFractionDigits: 1 })}
                      secondaryValue={`${formatMetric(getShare(vehicle.draining, drainSummary?.totalDrained) || 0, '%', { maximumFractionDigits: 1 })} of drained fuel | ${formatMetric(vehicle.mileage, 'km', { maximumFractionDigits: 1 })}`}
                      color={ANALYTICS_COLORS.danger}
                      progress={getShare(vehicle.draining, drainSummary?.totalDrained) || undefined}
                    />
                  ))}
                </Box>
              </>
            ) : null}
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FleetAnalytics;
